import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosInstance';
import { BASE_URL } from '@/config/constants';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import artistIntroLogoWhite from "../assets/artist_intro_white.svg";
import PixelBlast from '../components/PixelBlast';
import ForgotPassword from './ForgotPassword';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { LEGAL_URLS } from '@/lib/consent';

interface User {
  _id: string;
  username: string;
  user_type: string;
  email: string;
  domain: string;
}

const Login: React.FC<{ onLogin: (token: string, user: User, rememberMe?: boolean) => void; onSwitchToSignup: () => void }> = ({ onLogin, onSwitchToSignup }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [attemptWarning, setAttemptWarning] = useState('');
  // Account in the deletion grace period: login returns a recovery token
  // instead of a session; we show a restore screen.
  const [pendingDeletion, setPendingDeletion] = useState<{ scheduledFor: string; recoveryToken: string } | null>(null);
  const [cancellingDeletion, setCancellingDeletion] = useState(false);
  // Email-existence check (mirrors Signup): shows "not registered" hint for
  // unknown accounts. NOTE: this reintroduces a mild account-enumeration
  // oracle on login, accepted per product decision.
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'registered' | 'unregistered'>('idle');
  const emailCheckSeq = React.useRef(0);

  // Pre-fill saved username on mount
  useEffect(() => {
    const savedUsername = localStorage.getItem('remember_username');
    const remember = localStorage.getItem('remember_me') === 'true';

    if (remember && savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);

  // Debounced email-existence check (same pattern as Signup).
  useEffect(() => {
    const email = username.trim().toLowerCase();
    const seq = ++emailCheckSeq.current;

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailStatus('idle');
      return;
    }

    setEmailStatus('checking');
    const timer = window.setTimeout(async () => {
      try {
        const res = await axios.post(BASE_URL + '/api/auth/check-email', { email });
        if (seq !== emailCheckSeq.current) return;
        setEmailStatus(res.data?.available ? 'unregistered' : 'registered');
      } catch {
        if (seq !== emailCheckSeq.current) return;
        setEmailStatus('idle');
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [username]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setAttemptWarning('');
    setEmailStatus('idle');

    try {
      const res = await axios.post(BASE_URL + '/api/auth/login', { username, password });

      // Account is scheduled for deletion — offer recovery instead of a session.
      if (res.data?.accountPendingDeletion) {
        setPendingDeletion({
          scheduledFor: res.data.deletionScheduledFor,
          recoveryToken: res.data.recoveryToken,
        });
        return;
      }

      const { token, user } = res.data;

      // Save username preference (never password)
      if (rememberMe) {
        localStorage.setItem('remember_me', 'true');
        localStorage.setItem('remember_username', username);
      } else {
        localStorage.removeItem('remember_me');
        localStorage.removeItem('remember_username');
      }
      // Clean up legacy password storage
      localStorage.removeItem('remember_password');

      onLogin(token, user, rememberMe);
      toast.success('Login successful! Welcome back.');
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Login failed';

      // Surface remaining-attempts warning when the backend provides it.
      // In that case the warning banner carries the invalid-credentials text,
      // so we skip the separate error box to avoid duplication.
      const { attemptsRemaining, lockDurationMin } = err.response?.data || {};
      if (typeof attemptsRemaining === 'number' && attemptsRemaining > 0) {
        setAttemptWarning(
          `Invalid credentials. ${attemptsRemaining} ${attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining before the account is locked for ${lockDurationMin} minutes.`
        );
        setError('');
      } else {
        setAttemptWarning('');
        setError(errorMessage);
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 bg-white">
      {/* Branded Side */}
      <div className="hidden lg:flex flex-col justify-between bg-primary pt-20 pb-20 p-16 text-white overflow-hidden relative">
        <PixelBlast
          className="!absolute inset-0"
          color="#3b82f6"
          variant="circle"
          pixelSize={4}
          speed={0.3}
          patternDensity={1.8}
          edgeFade={0}
          enableRipples
          rippleSpeed={0.25}
          rippleThickness={0.12}
        />
        <div className="relative z-10">
          <img src={artistIntroLogoWhite} alt="Artist Intro" className="h-14" />
        </div>
        <div className="relative z-10">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Console</h1>
          <p className="text-base sm:text-lg text-white/80 max-w-md">
            Continue managing the artist's digital presence with our powerful admin dashboard.
          </p>
        </div>
      </div>

      {/* Form Side */}
      <div className="relative flex flex-col min-h-screen lg:min-h-0 bg-white">
        {/* Mobile branding header */}
        <div className="lg:hidden bg-primary px-6 py-5 shrink-0">
          <img src={artistIntroLogoWhite} alt="Artist Intro" className="h-7" />
        </div>


        <div className="flex-1 min-h-0 flex flex-col p-6 sm:p-12 lg:px-20 lg:pt-20 lg:pb-12">
          <div className="flex-1 flex items-center lg:items-start justify-center">
            <div className="w-full max-w-md space-y-8">
              {showForgotPassword ? (
            <ForgotPassword onBack={() => setShowForgotPassword(false)} />
          ) : pendingDeletion ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-center text-foreground">Account scheduled for deletion</h2>
                <p className="mt-2 text-sm text-muted-foreground text-center">
                  This account is deactivated and will be permanently deleted on{' '}
                  <strong className="text-foreground">
                    {new Date(pendingDeletion.scheduledFor).toLocaleDateString('en-US', {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  </strong>.
                </p>
              </div>
              <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 p-4">
                <p className="text-sm text-foreground">
                  You can still restore it. Cancelling the deletion reactivates your account and
                  public website. Manager access and cancelled subscriptions are not restored
                  automatically.
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  className="w-full"
                  loading={cancellingDeletion}
                  onClick={async () => {
                    setCancellingDeletion(true);
                    try {
                      await axios.post(
                        BASE_URL + '/api/auth/cancel-account-deletion',
                        {},
                        { headers: { Authorization: `Bearer ${pendingDeletion.recoveryToken}` } }
                      );
                      toast.success('Deletion cancelled — your account is restored. Please log in.');
                      setPendingDeletion(null);
                      setPassword('');
                    } catch (err: any) {
                      toast.error(err.response?.data?.message || 'Failed to cancel deletion');
                    } finally {
                      setCancellingDeletion(false);
                    }
                  }}
                >
                  Cancel deletion & restore my account
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={cancellingDeletion}
                  onClick={() => { setPendingDeletion(null); setPassword(''); }}
                >
                  Back to login
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-center text-foreground">Welcome back</h2>
                <p className="mt-2 text-sm text-muted-foreground text-center">
                   Enter your credentials to access the dashboard.
                </p>
              </div>
          
              <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                <div className="space-y-5">
                  <div>
                    <label htmlFor="username" className="block text-sm font-semibold text-foreground mb-2">
                      Username <span className="text-muted-foreground">(Registered email address)</span>
                    </label>
                    <Input
                      id="username"
                      type="text"
                      required
                      value={username}
                      onChange={e => { setUsername(e.target.value); setError(''); setAttemptWarning(''); }}
                      className=""
                      placeholder="Enter your email"
                    />
                    {emailStatus === 'unregistered' && (
                      <p className="mt-2 text-sm text-danger">
                        No account found with this email address{' '}
                        {/* <button
                          type="button"
                          onClick={onSwitchToSignup}
                          className="font-semibold underline hover:no-underline"
                        >
                          Create an account
                        </button> */}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-foreground mb-2">
                      Password
                    </label>
                    <PasswordInput
                      id="password"
                      required
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); setAttemptWarning(''); }}
                      placeholder="Enter your password"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Checkbox
                      id="remember-me"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked === true)}
                    />
                    <label htmlFor="remember-me" className="text-sm font-medium text-foreground cursor-pointer">
                      Remember me
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer"
                  >
                    Forgot password?
                  </button>
                </div>

                {error && (
                  <div className="text-sm text-danger bg-danger-light border border-danger/20 rounded-md p-4">
                    {error}
                  </div>
                )}

                {attemptWarning && !error && (
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-4">
                    {attemptWarning}
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg-full"
                  disabled={loading}
                  loading={loading}
                  loadingText="Signing in..."
                >
                  Log in
                </Button>
              </form>

              <p className="text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <button type="button" onClick={onSwitchToSignup} className="font-semibold text-primary hover:text-primary/80 transition-colors cursor-pointer">
                  Create one
                </button>
              </p>
            </>
          )}
          </div>
        </div>

        {/* Footer Links */}
        <div className="shrink-0 pt-4 flex flex-col items-center gap-2">
          {/* <p className="text-[10px] sm:text-xs text-muted-foreground text-center px-4">
            By continuing you agree to our Terms of Service and Privacy Policy.
          </p> */}
          <div className="flex justify-center gap-3 sm:gap-6 text-[10px] sm:text-xs tracking-wider text-muted-foreground">
            <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors uppercase">Terms of Service</a>
            <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors uppercase">Privacy Policy</a>
            <a href={LEGAL_URLS.cookies} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors uppercase">Cookie Policy</a>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
